import { useId, useState, type ReactNode } from 'react'
import { Dialog } from './dialog'
import { SpinnerGap } from './icons'

export type ConfirmDialogProps = {
  open: boolean
  /** Title Case question, for example "Delete Order Form OF-1043?". */
  title: string
  /** Sentence case prose that says what happens and whether it can be undone. */
  description?: ReactNode
  /** Title Case verb phrase, for example "Delete Order Form". */
  confirmLabel: string
  cancelLabel?: string
  /** Shown on the confirm button while `pending` is true. */
  pendingLabel?: string
  /** Styles the confirm button as a danger action. */
  destructive?: boolean
  /** Disables both buttons and blocks Escape while the action runs. */
  pending?: boolean
  /** When set, the person must type this exact text before the confirm button works. */
  confirmText?: string
  onConfirm: () => void
  onCancel: () => void
}

/** Asks the person to confirm an action. Every destructive action goes through it. */
export function ConfirmDialog({ open, title, description, onCancel, pending = false, ...rest }: ConfirmDialogProps) {
  return (
    <Dialog open={open} onClose={onCancel} title={title} description={description} dismissible={!pending} busy={pending} size="sm">
      <ConfirmContent onCancel={onCancel} pending={pending} {...rest} />
    </Dialog>
  )
}

function ConfirmContent({ confirmLabel, cancelLabel = 'Cancel', pendingLabel, destructive = false, pending, confirmText, onConfirm, onCancel }: Omit<ConfirmDialogProps, 'open' | 'title' | 'description'> & { pending: boolean }) {
  // This component mounts each time the dialog opens, so the typed text starts empty.
  const [typed, setTyped] = useState('')
  const inputId = useId()
  const matches = confirmText === undefined || typed.trim() === confirmText
  // aria-disabled, not disabled, keeps focus on the button while the action runs,
  // so focus never falls out of the dialog.
  const confirmBlocked = pending || !matches

  function confirm() {
    if (!confirmBlocked) onConfirm()
  }

  return (
    <>
      {confirmText !== undefined && (
        <div className="ui-confirm__type">
          <label htmlFor={inputId} className="ui-field__label">
            Type <strong className="ui-confirm__phrase">{confirmText}</strong> to confirm
          </label>
          <input
            id={inputId}
            className="ui-input"
            value={typed}
            readOnly={pending}
            autoComplete="off"
            spellCheck={false}
            data-autofocus=""
            onChange={(event) => setTyped(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); confirm() } }}
          />
        </div>
      )}
      <div className="ui-dialog__footer">
        <button
          type="button"
          className="button button-secondary"
          aria-disabled={pending || undefined}
          data-autofocus={destructive && confirmText === undefined ? '' : undefined}
          onClick={() => { if (!pending) onCancel() }}
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          className={['button', destructive ? 'button-primary ui-button-danger' : 'button-primary'].join(' ')}
          aria-disabled={confirmBlocked || undefined}
          data-autofocus={!destructive && confirmText === undefined ? '' : undefined}
          onClick={confirm}
        >
          {pending && <SpinnerGap size={16} className="ui-spinner" aria-hidden="true" />}
          {pending && pendingLabel ? pendingLabel : confirmLabel}
        </button>
      </div>
    </>
  )
}
